import React, { useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import { useAuth } from '../../context/AuthContext';
import { processService, repositoryService, fetchWithSessionHandling, sharingService, userService } from '../../services/api';
import FileExclusionModal from '../FileExclusionModal';
import MissingPackagesModal from '../MissingPackagesModal';

const ProjectConfigTab = ({ projectId, onClose }) => {
  const { deleteProject, projects, loadProjects } = useProject();
  const { user: currentUser } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [projectConfig, setProjectConfig] = useState({
    name: '',
    description: '',
    client: '',
    baName: '',
    saName: '',
    developers: ''
  });
  const [saveStatus, setSaveStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteProcessModal, setShowDeleteProcessModal] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);
  const [showAddProcessDisclaimerModal, setShowAddProcessDisclaimerModal] = useState(false);
  const [isProjectDetailsExpanded, setIsProjectDetailsExpanded] = useState(false);
  const [isProcessesExpanded, setIsProcessesExpanded] = useState(false);
  const [isDocumentsExpanded, setIsDocumentsExpanded] = useState(false);
  const [isSharingExpanded, setIsSharingExpanded] = useState(false);
  const [isSharedProject, setIsSharedProject] = useState(false);
  const [sharingWithAll, setSharingWithAll] = useState(true);
  const [sharingUserIds, setSharingUserIds] = useState(new Set());
  const [allShareableUsers, setAllShareableUsers] = useState([]);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [sharingSaving, setSharingSaving] = useState(false);
  const [sharingStatus, setSharingStatus] = useState('');
  const [sharingSearchQuery, setSharingSearchQuery] = useState('');

  // File exclusion modal state (for new process creation with manual upload)
  const [newProcessExclusion, setNewProcessExclusion] = useState({
    show: false,
    loading: false,
    data: null,       // { fileTree, totalFiles, projectName }
    uploadFile: null,  // the file being uploaded
    processId: null,   // temp process id in local state
    processData: null, // { name, description, ... }
    excludeCommentOutActivities: true,
    removeArgumentDefaultValues: false,
  });

  const [newProcessMissingPackages, setNewProcessMissingPackages] = useState({
    show: false,
    packages: [],
    pendingExclusion: null,
  });

  const getMissingPackagesFromPreview = (previewData) => {
    const allPackages = Array.isArray(previewData?.packageCheck?.packages)
      ? previewData.packageCheck.packages
      : [];
    const missingPackages = Array.isArray(previewData?.missingPackages)
      ? previewData.missingPackages
      : allPackages.filter((pkg) => String(pkg?.status || '').trim().toLowerCase() === 'not_found');

    console.log('[MissingDeps] preview package summary', {
      total: allPackages.length,
      missing: missingPackages.length,
    });

    return { allPackages, missingPackages };
  };

  const queueNewProcessExclusion = ({ fileTree, totalFiles, projectName, uploadFile, processId, processData }) => {
    setNewProcessExclusion({
      show: true,
      loading: false,
      data: { fileTree, totalFiles, projectName },
      uploadFile,
      processId,
      processData,
      excludeCommentOutActivities: true,
      removeArgumentDefaultValues: false,
    });
  };

  // Load project data when component mounts or projectId changes
  React.useEffect(() => {
    if (!projectId) return;
    
    const loadProjectData = async () => {
      try {
        const response = await fetchWithSessionHandling(`/api/projects/${projectId}`);
        const data = await response.json();
        
        if (data) {
          setProjectConfig({
            name: data.name || '',
            description: data.description || '',
            client: data.client || '',
            baName: data.baName || '',
            saName: data.saName || '',
            developers: data.developers || ''
          });

          // Check if this is a shared project (not in a personal folder)
          const projectIsShared = data.folder ? !data.folder.isPersonal : !data.folderId; // orphan projects are shared
          setIsSharedProject(projectIsShared);

          // Load sharing info for shared projects
          if (projectIsShared) {
            try {
              const [sharingRes, usersRes] = await Promise.all([
                sharingService.getProjectSharing(projectId),
                userService.getShareableUsers()
              ]);
              const sharingData = sharingRes.data || sharingRes;
              setSharingWithAll(sharingData.sharedWithAll);
              if (!sharingData.sharedWithAll) {
                setSharingUserIds(new Set(sharingData.users.map(u => u.id)));
              }
              setAllShareableUsers(usersRes.data || usersRes);
            } catch (err) {
              console.error('Error loading sharing data:', err);
            }
          }
        }

        // Load processes for this project
        const processesResponse = await processService.getProcessesByProject(projectId);
        setProcesses(processesResponse.data || []);
      } catch (error) {
        console.error('Error loading project data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
  }, [projectId]);

  const handleInputChange = (field, value) => {
    setProjectConfig(prev => ({ ...prev, [field]: value }));
  };

  const addDocument = () => {
    const newDoc = {
      id: Date.now(),
      type: 'link',
      url: '',
      description: '',
      isEditing: true
    };
    setDocuments(prev => [...prev, newDoc]);
  };

  const saveDocument = (docId, docData) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === docId ? { ...doc, ...docData, isEditing: false } : doc
    ));
  };

  const removeDocument = (docId) => {
    setDocuments(prev => prev.filter(doc => doc.id !== docId));
  };

  const editDocument = (docId) => {
    setDocuments(prev => prev.map(doc => 
      doc.id === docId ? { ...doc, isEditing: true } : doc
    ));
  };

  const cancelEdit = (docId) => {
    setDocuments(prev => prev.filter(doc => doc.id !== docId || !doc.isEditing));
  };

  const handleDeleteProcessClick = (process) => {
    setProcessToDelete(process);
    setShowDeleteProcessModal(true);
  };

  const handleConfirmDeleteProcess = async () => {
    if (!processToDelete) return;

    try {
      if (!processToDelete.isNew) {
        await processService.deleteProcess(processToDelete.id);
      }
      setProcesses(prev => prev.filter(p => p.id !== processToDelete.id));
      setShowDeleteProcessModal(false);
      setProcessToDelete(null);
      
      // Notify that processes have changed by dispatching a custom event
      window.dispatchEvent(new CustomEvent('processesChanged', { detail: { projectId } }));
    } catch (error) {
      console.error('Error deleting process:', error);
      alert('Failed to delete process: ' + error.message);
      setShowDeleteProcessModal(false);
      setProcessToDelete(null);
    }
  };

  const handleCancelDeleteProcess = () => {
    setShowDeleteProcessModal(false);
    setProcessToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!projectId) {
      setSaveStatus('❌ No project selected');
      setTimeout(() => setSaveStatus(''), 2500);
      return;
    }

    // Validate project name
    if (!projectConfig.name.trim()) {
      setSaveStatus('❌ Project name is required');
      setTimeout(() => setSaveStatus(''), 2500);
      return;
    }

    // Check for duplicate project name (excluding current project)
    const duplicateProject = projects.find(
      p => p.id !== projectId && p.name.toLowerCase() === projectConfig.name.trim().toLowerCase()
    );
    
    if (duplicateProject) {
      setSaveStatus('❌ Project name already exists');
      setTimeout(() => setSaveStatus(''), 2500);
      return;
    }

    setSaving(true);
    setSaveStatus('Saving...');

    try {
      // Save project configuration
      const response = await fetchWithSessionHandling(`/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(projectConfig)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save configuration');
      }

      const updatedProject = await response.json();
      
      // Update local state with saved data
      setProjectConfig({
        name: updatedProject.name || '',
        description: updatedProject.description || '',
        client: updatedProject.client || '',
        baName: updatedProject.baName || '',
        saName: updatedProject.saName || '',
        developers: updatedProject.developers || '',
        repositoryUrl: updatedProject.repositoryUrl || ''
      });

      // Reload projects list to update sidebar
      await loadProjects();

      setSaveStatus('✅ Saved successfully');
      
      // Close the configuration modal after successful save
      if (onClose) {
        setTimeout(() => {
          onClose();
        }, 500); // Brief delay to show success message
      } else {
        setTimeout(() => setSaveStatus(''), 2500);
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSaveStatus(`❌ ${error.message}`);
      setTimeout(() => setSaveStatus(''), 2500);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!projectId) return;

    try {
      // Reload project data from backend
      const response = await fetchWithSessionHandling(`/api/projects/${projectId}`);
      const data = await response.json();
      
      if (data) {
        setProjectConfig({
          name: data.name || '',
          description: data.description || '',
          client: data.client || '',
          baName: data.baName || '',
          saName: data.saName || '',
          developers: data.developers || ''
        });
      }

      setDocuments([]);
      setSaveStatus('✅ Reset to saved values');
      setTimeout(() => setSaveStatus(''), 1500);
    } catch (error) {
      console.error('Error resetting configuration:', error);
      setSaveStatus('❌ Failed to reset');
      setTimeout(() => setSaveStatus(''), 1500);
    }
  };

  const handleDeleteProject = async () => {
    if (!projectId) return;

    setDeleting(true);
    try {
      await deleteProject(projectId, { skipNavigate: true });
      alert('Project deleted successfully.');
      if (onClose) {
        onClose({ nextPath: '/' });
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Project Details Section */}
        <div className="p-6" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
          <div 
            className="flex items-center justify-between mb-4 pb-3 cursor-pointer"
            style={{borderBottom: '1px solid var(--tqa-border-dark)'}}
            onClick={() => setIsProjectDetailsExpanded(!isProjectDetailsExpanded)}
          >
            <div className="flex items-center">
              <div className="w-1 h-6 mr-3" style={{backgroundColor: 'var(--tqa-green)'}}></div>
              <h3 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>Project Details</h3>
            </div>
            <svg 
              className={`w-5 h-5 transition-transform ${isProjectDetailsExpanded ? 'rotate-180' : ''}`}
              style={{color: 'rgba(255,255,255,0.4)'}}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {isProjectDetailsExpanded && (
          <div className="space-y-4">
            <div>
              <label htmlFor="project-name" className="block text-sm font-semibold mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                Project Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="project-name"
                value={projectConfig.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-4 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
                placeholder="Enter project name"
              />
            </div>

            <div>
              <label htmlFor="project-desc" className="block text-sm font-semibold mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                Project Description
              </label>
              <textarea
                id="project-desc"
                rows="3"
                value={projectConfig.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-4 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
                placeholder="Used by chat to guide responses for this project. Include project type (e.g., REFramework, DU, Dispatcher/Performer) and any special instructions the agent should follow."
              >
              </textarea>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="client" className="block text-sm font-semibold mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                  Client
                </label>
                <input
                  type="text"
                  id="client"
                  value={projectConfig.client}
                  onChange={(e) => handleInputChange('client', e.target.value)}
                  className="w-full px-4 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
                />
              </div>
              
              <div>
                <label htmlFor="ba-name" className="block text-sm font-semibold mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                  Business Analyst
                </label>
                <input
                  type="text"
                  id="ba-name"
                  value={projectConfig.baName}
                  onChange={(e) => handleInputChange('baName', e.target.value)}
                  className="w-full px-4 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
                />
              </div>
              
              <div>
                <label htmlFor="sa-name" className="block text-sm font-semibold mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                  Solution Architect
                </label>
                <input
                  type="text"
                  id="sa-name"
                  value={projectConfig.saName}
                  onChange={(e) => handleInputChange('saName', e.target.value)}
                  className="w-full px-4 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
                />
              </div>
              
              <div>
                <label htmlFor="developers" className="block text-sm font-semibold mb-2" style={{color: 'rgba(255,255,255,0.65)'}}>
                  Developers (comma-separated)
                </label>
                <input
                  type="text"
                  id="developers"
                  value={projectConfig.developers}
                  onChange={(e) => handleInputChange('developers', e.target.value)}
                  className="w-full px-4 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
                  placeholder="Dev 1, Dev 2, ..."
                />
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Processes Section */}
        <div className="p-6" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
          <div 
            className="flex items-center justify-between mb-4 pb-3 cursor-pointer"
            style={{borderBottom: '1px solid var(--tqa-border-dark)'}}
            onClick={() => setIsProcessesExpanded(!isProcessesExpanded)}
          >
            <div className="flex items-center">
              <div className="w-1 h-6 mr-3" style={{backgroundColor: 'var(--tqa-green)'}}></div>
              <h3 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>Project Code (Processes)</h3>
            </div>
            <svg 
              className={`w-5 h-5 transition-transform ${isProcessesExpanded ? 'rotate-180' : ''}`}
              style={{color: 'rgba(255,255,255,0.4)'}}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {isProcessesExpanded && (
          <>
          <div className="flex items-center justify-between mb-4">
            <div />
            <button 
              type="button" 
              onClick={(e) => {
                e.stopPropagation();
                setShowAddProcessDisclaimerModal(true);
              }}
              className="btn-secondary text-xs px-4 py-2"
            >
              + Add Process
            </button>
          </div>
          
          <p className="text-xs mb-4" style={{color: 'rgba(255,255,255,0.4)'}}>
            Add one or more processes (repositories) to this project. Each process represents an independent codebase, such as a Dispatcher or Performer.
          </p>
          
          {processes.length > 0 ? (
            <div className="space-y-4">
              {processes.map((process, index) => (
                <ProcessItem 
                  key={process.id}
                  process={process}
                  projectId={projectId}
                  onUpdate={async (updatedProcess, uploadFile) => {
                    if (updatedProcess.isNew) {
                      // Create new process
                      try {
                        // For manual uploads, preview structure first, then create
                        if (updatedProcess.sourceType === 'manual' && uploadFile) {
                          // Show upload indicator
                          setProcesses(prev => prev.map(p => 
                            p.id === process.id ? { ...p, isUploading: true } : p
                          ));

                          try {
                            // Step 1: Preview the structure
                            const previewRes = await processService.previewStructure(uploadFile);
                            const previewData = previewRes.data.data;
                            const { fileTree, totalFiles, projectName } = previewData;

                            // Hide uploading indicator
                            setProcesses(prev => prev.map(p => 
                              p.id === process.id ? { ...p, isUploading: false } : p
                            ));

                            const { allPackages, missingPackages } = getMissingPackagesFromPreview(previewData);

                            if (missingPackages.length > 0) {
                              setNewProcessMissingPackages({
                                show: true,
                                packages: allPackages,
                                pendingExclusion: {
                                  fileTree,
                                  totalFiles,
                                  projectName,
                                  uploadFile,
                                  processId: process.id,
                                  processData: updatedProcess,
                                },
                              });
                              return;
                            }

                            queueNewProcessExclusion({
                              fileTree,
                              totalFiles,
                              projectName,
                              uploadFile,
                              processId: process.id,
                              processData: updatedProcess,
                            });
                            return;

                          } catch (uploadError) {
                            console.error('Preview failed:', uploadError);
                            const msg = uploadError.response?.data?.message || uploadError.message || 'Unknown error';
                            alert('Upload Failed\n\n' + msg);
                            setProcesses(prev => prev.filter(p => p.id !== process.id));
                            return;
                          }
                        }

                        // For repository type, create process first, then sync
                        const response = await processService.createProcess({
                          projectId,
                          name: updatedProcess.name,
                          description: updatedProcess.description,
                          repositoryUrl: updatedProcess.repositoryUrl || ''
                        });
                        const newProcess = response.data;
                        setProcesses(prev => prev.map(p => 
                          p.id === process.id ? { ...newProcess, isEditing: false, isSyncing: updatedProcess.sourceType === 'repository' } : p
                        ));
                        
                        // Notify that processes have changed
                        window.dispatchEvent(new CustomEvent('processesChanged', { detail: { projectId } }));

                        // Handle repository auto-sync
                        if (updatedProcess.sourceType === 'repository') {
                          // Preview structure first, then show exclusion modal
                          try {
                            setProcesses(prev => prev.map(p => 
                              p.id === newProcess.id ? { ...p, isSyncing: true } : p
                            ));

                            const previewRes = await repositoryService.previewStructure(updatedProcess.repositoryUrl);
                            const previewData = previewRes.data.data;
                            const { fileTree, totalFiles, projectName } = previewData;

                            setProcesses(prev => prev.map(p => 
                              p.id === newProcess.id ? { ...p, isSyncing: false } : p
                            ));

                            const { allPackages, missingPackages } = getMissingPackagesFromPreview(previewData);

                            if (missingPackages.length > 0) {
                              setNewProcessMissingPackages({
                                show: true,
                                packages: allPackages,
                                pendingExclusion: {
                                  fileTree,
                                  totalFiles,
                                  projectName,
                                  uploadFile: null,
                                  processId: newProcess.id,
                                  processData: { ...updatedProcess, _repoSync: true, _newProcessObj: newProcess },
                                },
                              });
                            } else {
                              queueNewProcessExclusion({
                                fileTree,
                                totalFiles,
                                projectName,
                                uploadFile: null,
                                processId: newProcess.id,
                                processData: { ...updatedProcess, _repoSync: true, _newProcessObj: newProcess },
                              });
                            }

                          } catch (previewError) {
                            console.error('Preview failed:', previewError);
                            setProcesses(prev => prev.map(p => 
                              p.id === newProcess.id ? { ...p, isSyncing: false } : p
                            ));
                          }
                        }
                      } catch (error) {
                        console.error('Error creating process:', error);
                        alert('Failed to create process: ' + error.message);
                      }
                    } else {
                      // Update existing process
                      try {
                        // Only send allowed fields to API (filter out read-only fields)
                        const updatePayload = {
                          name: updatedProcess.name,
                          description: updatedProcess.description,
                          repositoryUrl: updatedProcess.repositoryUrl
                        };
                        
                        // Include optional fields if they exist in updatedProcess
                        if (updatedProcess.technicalName !== undefined) {
                          updatePayload.technicalName = updatedProcess.technicalName;
                        }
                        if (updatedProcess.version !== undefined) {
                          updatePayload.version = updatedProcess.version;
                        }
                        if (updatedProcess.lastSyncCommit !== undefined) {
                          updatePayload.lastSyncCommit = updatedProcess.lastSyncCommit;
                        }
                        if (updatedProcess.lastSyncDate !== undefined) {
                          updatePayload.lastSyncDate = updatedProcess.lastSyncDate;
                        }
                        
                        // Update basic process info
                        await processService.updateProcess(process.id, updatePayload);
                        
                        // If there's a file upload for manual process, preview structure first
                        if (uploadFile && !process.repositoryUrl) {
                          setProcesses(prev => prev.map(p => 
                            p.id === process.id ? { ...p, isUploading: true, isEditing: false } : p
                          ));

                          try {
                            // Step 1: Preview the structure
                            const previewRes = await processService.previewStructure(uploadFile);
                            const previewData = previewRes.data.data;
                            const { fileTree, totalFiles, projectName } = previewData;

                            // Hide uploading indicator
                            setProcesses(prev => prev.map(p => 
                              p.id === process.id ? { ...p, isUploading: false } : p
                            ));

                            const { allPackages, missingPackages } = getMissingPackagesFromPreview(previewData);

                            if (missingPackages.length > 0) {
                              setNewProcessMissingPackages({
                                show: true,
                                packages: allPackages,
                                pendingExclusion: {
                                  fileTree,
                                  totalFiles,
                                  projectName,
                                  uploadFile,
                                  processId: process.id,
                                  processData: { ...updatedProcess, _existingEdit: true },
                                },
                              });
                              return;
                            }

                            queueNewProcessExclusion({
                              fileTree,
                              totalFiles,
                              projectName,
                              uploadFile,
                              processId: process.id,
                              processData: { ...updatedProcess, _existingEdit: true },
                            });
                            return;

                          } catch (uploadError) {
                            console.error('Preview failed:', uploadError);
                            alert('Upload failed: ' + (uploadError.message || 'Unknown error'));
                            setProcesses(prev => prev.map(p => 
                              p.id === process.id ? { ...p, isUploading: false } : p
                            ));
                          }
                        } else {
                          // No upload, just update UI
                          setProcesses(prev => prev.map(p => 
                            p.id === process.id ? { ...p, ...updatedProcess, isEditing: false } : p
                          ));
                        }
                      } catch (error) {
                        console.error('Error updating process:', error);
                        alert('Failed to update process: ' + error.message);
                      }
                    }
                  }}
                  onDelete={async () => {
                    if (process.isNew) {
                      setProcesses(prev => prev.filter(p => p.id !== process.id));
                    } else {
                      handleDeleteProcessClick(process);
                    }
                  }}
                  onEdit={() => {
                    setProcesses(prev => prev.map(p => 
                      p.id === process.id ? { ...p, isEditing: true } : p
                    ));
                  }}
                  onCancel={() => {
                    if (process.isNew) {
                      setProcesses(prev => prev.filter(p => p.id !== process.id));
                    } else {
                      setProcesses(prev => prev.map(p => 
                        p.id === process.id ? { ...p, isEditing: false } : p
                      ));
                    }
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8" style={{backgroundColor: 'transparent', border: '1px dashed var(--tqa-border-dark)'}}>
              <p className="text-sm" style={{color: 'rgba(255,255,255,0.4)'}}>No processes added yet</p>
              <p className="text-xs mt-1" style={{color: 'rgba(255,255,255,0.3)'}}>Click "Add Process" to link a repository</p>
            </div>
          )}
          </>
          )}
        </div>

        {/* Project Documents Section */}
        <div className="p-6" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
          <div 
            className="flex items-center justify-between mb-4 pb-3 cursor-pointer"
            style={{borderBottom: '1px solid var(--tqa-border-dark)'}}
            onClick={() => setIsDocumentsExpanded(!isDocumentsExpanded)}
          >
            <div className="flex items-center gap-2">
              <div className="w-1 h-6 mr-3" style={{backgroundColor: 'var(--tqa-green)'}}></div>
              <h3 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>Project Documents</h3>
              <span className="text-xs px-1.5 py-0.5 font-medium text-center leading-tight" style={{backgroundColor: 'rgba(245,158,11,0.15)', color: 'rgba(245,158,11,0.8)', border: '1px solid rgba(245,158,11,0.3)'}}>
                Coming<br/>Soon
              </span>
            </div>
            <svg 
              className={`w-5 h-5 transition-transform ${isDocumentsExpanded ? 'rotate-180' : ''}`}
              style={{color: 'rgba(255,255,255,0.4)'}}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {isDocumentsExpanded && (
          <>
          <div className="flex items-center justify-between mb-4">
            <div />
            <button 
              type="button" 
              onClick={(e) => {
                e.stopPropagation();
                addDocument();
              }}
              className="btn-secondary text-xs px-4 py-2"
            >
              + Add Document
            </button>
          </div>
          
          <p className="text-xs mb-4" style={{color: 'rgba(255,255,255,0.4)'}}>
            Attach internal files or reference external URLs and provide a short description. Unlimited entries.
          </p>
          
          {documents.length > 0 ? (
            <ul className="" style={{borderTop: '1px solid var(--tqa-border-dark)', border: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface-2)'}}>
              {documents.map(doc => (
                <DocumentItem 
                  key={doc.id}
                  document={doc}
                  onSave={saveDocument}
                  onEdit={editDocument}
                  onRemove={removeDocument}
                  onCancel={cancelEdit}
                />
              ))}
            </ul>
          ) : (
            <div className="text-center py-8" style={{border: '1px dashed var(--tqa-border-dark)'}}>
              <p className="text-sm" style={{color: 'rgba(255,255,255,0.4)'}}>No documents added yet</p>
              <p className="text-xs mt-1" style={{color: 'rgba(255,255,255,0.3)'}}>Click "Add Document" to attach files or links</p>
            </div>
          )}
          </>
          )}
        </div>

        {/* Sharing Section (only for shared projects) */}
        {isSharedProject && (
        <div className="p-6" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
          <div
            className="flex items-center justify-between mb-4 pb-3 cursor-pointer"
            style={{borderBottom: '1px solid var(--tqa-border-dark)'}}
            onClick={() => setIsSharingExpanded(!isSharingExpanded)}
          >
            <div className="flex items-center">
              <div className="w-1 h-6 mr-3" style={{backgroundColor: 'var(--tqa-green)'}}></div>
              <h3 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>Sharing</h3>
              {!sharingWithAll && (
                <span className="ml-3 text-xs px-2 py-0.5" style={{backgroundColor: 'rgba(130,255,207,0.1)', color: 'var(--tqa-green)', border: '1px solid rgba(130,255,207,0.2)'}}>
                  {sharingUserIds.size} users
                </span>
              )}
            </div>
            <svg
              className={`w-5 h-5 transition-transform ${isSharingExpanded ? 'rotate-180' : ''}`}
              style={{color: 'rgba(255,255,255,0.4)'}}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {isSharingExpanded && (
          <div>
            <p className="text-xs mb-4" style={{color: 'rgba(255,255,255,0.4)'}}>
              Control who can see this project. Changes are saved immediately.
            </p>

            {/* Share with everyone toggle */}
            <label className="flex items-center gap-3 mb-4 cursor-pointer select-none">
              <div
                className="relative w-10 h-5 rounded-full transition-colors"
                style={{ backgroundColor: sharingWithAll ? 'var(--tqa-green)' : 'var(--tqa-surface-3)' }}
                onClick={() => setSharingWithAll(!sharingWithAll)}
              >
                <div
                  className="absolute top-0.5 w-4 h-4 rounded-full transition-transform"
                  style={{ backgroundColor: 'white', transform: sharingWithAll ? 'translateX(22px)' : 'translateX(2px)' }}
                />
              </div>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Share with everyone
              </span>
            </label>

            {/* User selection */}
            {!sharingWithAll && (
              <div>
                {/* Search */}
                <input
                  type="text"
                  value={sharingSearchQuery}
                  onChange={(e) => setSharingSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 text-sm text-white focus:outline-none mb-2"
                  style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' }}
                  placeholder="Search users..."
                />

                {/* Select all / deselect all */}
                <div className="flex gap-3 mb-2">
                  <button type="button" onClick={() => { const all = new Set(allShareableUsers.filter(u => u.id !== currentUser?.id).map(u => u.id)); if (currentUser?.id) all.add(currentUser.id); setSharingUserIds(all); }}
                    className="text-xs uppercase tracking-wider transition-colors hover:brightness-125" style={{ color: 'var(--tqa-green)' }}>
                    Select all
                  </button>
                  <button type="button" onClick={() => setSharingUserIds(new Set(currentUser?.id ? [currentUser.id] : []))}
                    className="text-xs uppercase tracking-wider transition-colors hover:brightness-125" style={{ color: 'rgba(255,255,255,0.5)' }}>
                    Deselect all
                  </button>
                </div>

                {/* User list */}
                <div className="max-h-64 overflow-y-auto" style={{ backgroundColor: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)', scrollbarWidth: 'thin' }}>
                  {allShareableUsers
                    .filter(u => u.id !== currentUser?.id)
                    .filter(u => {
                      if (!sharingSearchQuery.trim()) return true;
                      const q = sharingSearchQuery.toLowerCase();
                      return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
                    })
                    .sort((a, b) => {
                      const aSel = sharingUserIds.has(a.id) ? 1 : 0;
                      const bSel = sharingUserIds.has(b.id) ? 1 : 0;
                      return bSel - aSel;
                    })
                    .map(user => (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:brightness-110"
                      style={{ backgroundColor: sharingUserIds.has(user.id) ? 'rgba(130,255,207,0.06)' : 'transparent' }}
                    >
                      <input
                        type="checkbox"
                        checked={sharingUserIds.has(user.id)}
                        onChange={() => setSharingUserIds(prev => { const n = new Set(prev); n.has(user.id) ? n.delete(user.id) : n.add(user.id); return n; })}
                        className="accent-[var(--tqa-green)] w-4 h-4 flex-shrink-0"
                      />
                      {user.avatarUrl ? (
                        <img src={user.avatarUrl} alt="" className="w-6 h-6 rounded-full flex-shrink-0" />
                      ) : (
                        <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold"
                          style={{ backgroundColor: 'var(--tqa-surface-3)', color: 'rgba(255,255,255,0.5)' }}>
                          {(user.name || user.email || '?')[0].toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white truncate">{user.name || 'Unnamed'}</div>
                        <div className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{user.email}</div>
                      </div>
                    </label>
                    ))}
                </div>

                <div className="mt-2 text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  {sharingUserIds.size - (currentUser?.id && sharingUserIds.has(currentUser.id) ? 1 : 0)} of {allShareableUsers.filter(u => u.id !== currentUser?.id).length} users selected
                </div>
              </div>
            )}

            {/* Save sharing button */}
            <div className="flex items-center gap-3 mt-4">
              <button
                type="button"
                disabled={sharingSaving || (!sharingWithAll && sharingUserIds.size === 0)}
                onClick={async () => {
                  setSharingSaving(true);
                  setSharingStatus('');
                  try {
                    const userIds = sharingWithAll ? [] : Array.from(sharingUserIds);
                    await sharingService.updateProjectSharing(projectId, userIds);
                    await loadProjects();
                    setSharingStatus('Saved');
                    setTimeout(() => setSharingStatus(''), 3000);
                  } catch (err) {
                    setSharingStatus('Failed to save');
                    console.error('Error saving sharing:', err);
                  } finally {
                    setSharingSaving(false);
                  }
                }}
                className="px-6 py-2 text-sm font-medium uppercase tracking-wider transition-colors hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: 'var(--tqa-green)', color: '#1a1a1a' }}
              >
                {sharingSaving ? 'Saving...' : 'Save Sharing'}
              </button>
              {sharingStatus && <span className="text-sm" style={{color: 'rgba(255,255,255,0.5)'}}>{sharingStatus}</span>}
            </div>
          </div>
          )}
        </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center space-x-3">
            <button 
              type="submit" 
              className="btn-primary px-8 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            <button 
              type="button" 
              onClick={handleReset} 
              className="btn-secondary px-8 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving}
            >
              Reset
            </button>
            {saveStatus && <span className="text-sm ml-2" style={{color: 'rgba(255,255,255,0.5)'}}>{saveStatus}</span>}
          </div>
          
          {/* Delete Project Button */}
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="px-6 py-2.5 text-sm font-medium" style={{color: 'var(--tqa-red)', backgroundColor: 'rgba(230,100,97,0.08)', border: '1px solid rgba(230,100,97,0.3)'}}
          >
            Delete Project
          </button>
        </div>
      </form>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="shadow-xl p-6 w-96" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
            <h3 className="text-lg font-semibold mb-2" style={{color: 'rgba(255,255,255,0.9)'}}>Delete Project</h3>
            <p className="text-sm mb-4" style={{color: 'rgba(255,255,255,0.5)'}}>
              Are you sure you want to delete this project? This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary px-4 py-2 text-sm font-medium"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteProject}
                className="px-4 py-2 text-sm font-medium text-white disabled:opacity-50" style={{backgroundColor: 'var(--tqa-red)'}}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Process Confirmation Modal */}
      <DeleteProcessModal
        isOpen={showDeleteProcessModal}
        processName={processToDelete?.name || ''}
        onConfirm={handleConfirmDeleteProcess}
        onCancel={handleCancelDeleteProcess}
      />

      {/* Add Process Disclaimer Modal */}
      <SyncDisclaimerModal
        isOpen={showAddProcessDisclaimerModal}
        onConfirm={() => {
          setShowAddProcessDisclaimerModal(false);
          // Add new process after confirmation
          const newProcess = {
            id: `temp-${Date.now()}`,
            name: '',
            description: '',
            repositoryUrl: '',
            isEditing: true,
            isNew: true
          };
          setProcesses(prev => [...prev, newProcess]);
        }}
        onCancel={() => {
          setShowAddProcessDisclaimerModal(false);
        }}
      />

      <MissingPackagesModal
        isOpen={newProcessMissingPackages.show}
        packages={newProcessMissingPackages.packages}
        onClose={() => {
          const pending = newProcessMissingPackages.pendingExclusion;
          setNewProcessMissingPackages({ show: false, packages: [], pendingExclusion: null });

          if (pending?.processId && String(pending.processId).startsWith('temp-')) {
            setProcesses(prev => prev.filter(p => p.id !== pending.processId));
          }
        }}
        onContinue={() => {
          const pending = newProcessMissingPackages.pendingExclusion;
          setNewProcessMissingPackages({ show: false, packages: [], pendingExclusion: null });

          if (!pending) {
            return;
          }

          queueNewProcessExclusion({
            fileTree: pending.fileTree,
            totalFiles: pending.totalFiles,
            projectName: pending.projectName,
            uploadFile: pending.uploadFile,
            processId: pending.processId,
            processData: pending.processData,
          });
        }}
      />

      {/* File Exclusion Modal for process creation and editing */}
      <FileExclusionModal
        isOpen={newProcessExclusion.show}
        onClose={() => {
          setNewProcessExclusion(prev => ({ ...prev, show: false }));
          // Only remove temp process for new processes, not existing edits
          if (newProcessExclusion.processId && !newProcessExclusion.processData?._existingEdit) {
            setProcesses(prev => prev.filter(p => p.id !== newProcessExclusion.processId));
          }
        }}
        onConfirm={async ({ excludedFiles, excludeCommentOutActivities, removeArgumentDefaultValues }) => {
          const { uploadFile, processData, processId: tempId } = newProcessExclusion;
          setNewProcessExclusion(prev => ({ ...prev, loading: true }));
          
          try {
            // Check if this is a repo sync or manual upload
            if (processData?._repoSync) {
              // Repository sync with exclusions
              const newProcess = processData._newProcessObj;
              setProcesses(prev => prev.map(p => 
                p.id === newProcess.id ? { ...p, isSyncing: true } : p
              ));

              const syncResponse = await fetchWithSessionHandling('/api/repository/sync', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  projectId,
                  processId: newProcess.id,
                  repoUrl: processData.repositoryUrl,
                  excludedFiles,
                  excludeCommentOutActivities,
                  removeArgumentDefaultValues,
                })
              });

              const syncData = await syncResponse.json();

              if (syncData.success) {
                setProcesses(prev => prev.map(p => 
                  p.id === newProcess.id ? {
                    ...newProcess,
                    technicalName: syncData.data.technicalName,
                    version: syncData.data.version,
                    lastSyncCommit: syncData.data.commitInfo.hash,
                    lastSyncDate: syncData.data.syncDate,
                    isSyncing: false
                  } : p
                ));
              } else {
                setProcesses(prev => prev.map(p => 
                  p.id === newProcess.id ? { ...p, isSyncing: false } : p
                ));
              }
            } else if (processData?._existingEdit) {
              // Existing process edit — upload with exclusions
              const existingId = tempId;
              setProcesses(prev => prev.map(p => 
                p.id === existingId ? { ...p, isUploading: true } : p
              ));

              const editFormData = new FormData();
              editFormData.append('processFile', uploadFile);
              if (excludedFiles.length > 0) {
                editFormData.append('excludedFiles', JSON.stringify(excludedFiles));
              }
              editFormData.append('excludeCommentOutActivities', String(excludeCommentOutActivities));
              editFormData.append('removeArgumentDefaultValues', String(removeArgumentDefaultValues));

              const uploadResponse = await fetchWithSessionHandling(`/api/processes/${existingId}/upload`, {
                method: 'POST',
                body: editFormData
              });

              const uploadData = await uploadResponse.json();

              if (uploadData.success) {
                const processResponse = await processService.getProcess(existingId);
                const updatedProcessData = processResponse.data;
                setProcesses(prev => prev.map(p => 
                  p.id === existingId ? { ...updatedProcessData, isUploading: false, isEditing: false } : p
                ));
              } else {
                alert('Upload Failed\n\n' + (uploadData.message || 'Unknown error'));
                setProcesses(prev => prev.map(p => 
                  p.id === existingId ? { ...p, isUploading: false } : p
                ));
              }
            } else {
              // New process — manual upload with exclusions
              const tempFormData = new FormData();
              tempFormData.append('processFile', uploadFile);
              tempFormData.append('projectId', projectId);
              tempFormData.append('name', processData.name);
              tempFormData.append('description', processData.description || '');
              if (excludedFiles.length > 0) {
                tempFormData.append('excludedFiles', JSON.stringify(excludedFiles));
              }
              tempFormData.append('excludeCommentOutActivities', String(excludeCommentOutActivities));
              tempFormData.append('removeArgumentDefaultValues', String(removeArgumentDefaultValues));

              const uploadResponse = await fetchWithSessionHandling('/api/processes/create-with-upload', {
                method: 'POST',
                body: tempFormData
              });

              const uploadData = await uploadResponse.json();

              if (!uploadResponse.ok || !uploadData.success) {
                const errorMsg = uploadData.message || uploadData.error || 'Unknown error';
                alert('Upload Failed\n\n' + errorMsg);
                setProcesses(prev => prev.filter(p => p.id !== tempId));
              } else {
                const newProcess = uploadData.data.process;
                setProcesses(prev => prev.map(p => 
                  p.id === tempId ? { ...newProcess, isEditing: false, isUploading: false } : p
                ));
                window.dispatchEvent(new CustomEvent('processesChanged', { detail: { projectId } }));
              }
            }
          } catch (error) {
            console.error('Processing failed:', error);
            alert('Processing failed: ' + (error.message || 'Unknown error'));
            setProcesses(prev => prev.filter(p => p.id !== tempId));
          } finally {
            setNewProcessExclusion({ show: false, loading: false, data: null, uploadFile: null, processId: null, processData: null, excludeCommentOutActivities: true, removeArgumentDefaultValues: false });
          }
        }}
        fileTree={newProcessExclusion.data?.fileTree}
        projectName={newProcessExclusion.data?.projectName}
        totalFiles={newProcessExclusion.data?.totalFiles || 0}
        loading={newProcessExclusion.loading}
      />
    </div>
  );
};

// Process Item Component
const ProcessItem = ({ process, projectId, onUpdate, onDelete, onEdit, onCancel }) => {
  const [editData, setEditData] = useState({
    name: process.name || '',
    description: process.description || '',
    repositoryUrl: process.repositoryUrl || '',
    sourceType: process.repositoryUrl ? 'repository' : (process.isNew ? '' : 'manual') // 'repository' or 'manual'
  });
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [showSyncDisclaimerModal, setShowSyncDisclaimerModal] = useState(false);
  const [pendingSyncAction, setPendingSyncAction] = useState(null);
  
  // File exclusion modal state
  const [showFileExclusion, setShowFileExclusion] = useState(false);
  const [fileExclusionData, setFileExclusionData] = useState(null); // { fileTree, totalFiles, projectName }
  const [fileExclusionLoading, setFileExclusionLoading] = useState(false);
  const [pendingExclusionAction, setPendingExclusionAction] = useState(null); // fn({ excludedFiles, excludeCommentOutActivities, removeArgumentDefaultValues })
  const [showMissingPackagesModal, setShowMissingPackagesModal] = useState(false);
  const [previewPackageList, setPreviewPackageList] = useState([]);

  const getMissingPackagesFromPreview = (previewData) => {
    const allPackages = Array.isArray(previewData?.packageCheck?.packages)
      ? previewData.packageCheck.packages
      : [];
    const missingPackages = Array.isArray(previewData?.missingPackages)
      ? previewData.missingPackages
      : allPackages.filter((pkg) => String(pkg?.status || '').trim().toLowerCase() === 'not_found');

    console.log('[MissingDeps][ProcessItem] preview package summary', {
      total: allPackages.length,
      missing: missingPackages.length,
    });

    return { allPackages, missingPackages };
  };

  const handleSave = () => {
    if (!editData.name.trim()) {
      alert('Process name is required');
      return;
    }
    
    // For new processes, validate based on source type
    if (process.isNew) {
      if (!editData.sourceType) {
        alert('Please select a source type (Repository URL or Manual Upload)');
        return;
      }
      if (editData.sourceType === 'repository' && !editData.repositoryUrl.trim()) {
        alert('Repository URL is required');
        return;
      }
      if (editData.sourceType === 'manual' && !uploadFile) {
        alert('Please select a file to upload');
        return;
      }
    } else {
      // For existing processes being edited, just validate repository URL if present
      if (editData.repositoryUrl && !editData.repositoryUrl.trim()) {
        alert('Repository URL cannot be empty');
        return;
      }
    }
    
    onUpdate({ ...editData, isNew: process.isNew }, uploadFile);
  };

  const handleSync = async () => {
    if (!editData.repositoryUrl.trim()) {
      setSyncStatus('❌ Please enter a repository URL');
      return;
    }

    // Show disclaimer modal before syncing
    setPendingSyncAction(() => previewBeforeSync);
    setShowSyncDisclaimerModal(true);
  };

  // Step 1: Preview repo structure, then show file exclusion modal
  const previewBeforeSync = async () => {
    setSyncing(true);
    setSyncStatus('🔍 Downloading and analyzing repository structure...');
    try {
      const res = await repositoryService.previewStructure(editData.repositoryUrl);
      const previewData = res.data.data;
      const { fileTree, totalFiles, projectName } = previewData;
      setFileExclusionData({ fileTree, totalFiles, projectName });
      setPendingExclusionAction(() => ({ excludedFiles, excludeCommentOutActivities, removeArgumentDefaultValues }) => finalizeSync(excludedFiles, excludeCommentOutActivities, removeArgumentDefaultValues));
      const { allPackages, missingPackages } = getMissingPackagesFromPreview(previewData);

      if (missingPackages.length > 0) {
        setPreviewPackageList(allPackages);
        setShowMissingPackagesModal(true);
      } else {
        setShowFileExclusion(true);
      }
      setSyncStatus('');
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setSyncStatus(`❌ ${msg}`);
    } finally {
      setSyncing(false);
    }
  };

  // Step 2: Actually sync with exclusions
  const finalizeSync = async (excludedFiles, excludeCommentOutActivities = true, removeArgumentDefaultValues = false) => {
    setFileExclusionLoading(true);
    setSyncing(true);
    setSyncStatus('🔍 Syncing repository...');

    try {
      const response = await fetchWithSessionHandling('/api/repository/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: projectId,
          processId: process.id,
          repoUrl: editData.repositoryUrl,
          excludedFiles: excludedFiles,
          excludeCommentOutActivities,
          removeArgumentDefaultValues
        })
      });

      const data = await response.json();

      if (data.success) {
        setSyncStatus(`✅ Successfully synced ${data.data.parsedFiles} workflow files!`);
        setShowFileExclusion(false);
        setFileExclusionData(null);
        
        if (!process.isEditing) {
          onUpdate({
            ...process,
            technicalName: data.data.technicalName,
            version: data.data.version,
            lastSyncCommit: data.data.commitInfo.hash,
            lastSyncDate: data.data.syncDate
          });
        }
      } else {
        setSyncStatus(`❌ ${data.message || 'Sync failed'}`);
      }
    } catch (error) {
      setSyncStatus(`❌ Error: ${error.message}`);
    } finally {
      setSyncing(false);
      setFileExclusionLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setSyncStatus('❌ Please select a file to upload');
      return;
    }

    // Show disclaimer modal before uploading
    setPendingSyncAction(() => previewBeforeUpload);
    setShowSyncDisclaimerModal(true);
  };

  // Step 1: Preview structure, then show file exclusion modal
  const previewBeforeUpload = async () => {
    setUploading(true);
    setSyncStatus('🔍 Analyzing project structure...');
    try {
      const res = await processService.previewStructure(uploadFile);
      const previewData = res.data.data;
      const { fileTree, totalFiles, projectName } = previewData;
      setFileExclusionData({ fileTree, totalFiles, projectName });
      setPendingExclusionAction(() => ({ excludedFiles, excludeCommentOutActivities, removeArgumentDefaultValues }) => finalizeUpload(excludedFiles, excludeCommentOutActivities, removeArgumentDefaultValues));
      const { allPackages, missingPackages } = getMissingPackagesFromPreview(previewData);

      if (missingPackages.length > 0) {
        setPreviewPackageList(allPackages);
        setShowMissingPackagesModal(true);
      } else {
        setShowFileExclusion(true);
      }
      setSyncStatus('');
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setSyncStatus(`❌ ${msg}`);
    } finally {
      setUploading(false);
    }
  };

  // Step 2: Actually upload with exclusions
  const finalizeUpload = async (excludedFiles, excludeCommentOutActivities = true, removeArgumentDefaultValues = false) => {
    setFileExclusionLoading(true);
    setUploading(true);
    setSyncStatus('📤 Uploading and processing file...');

    try {
      const formData = new FormData();
      formData.append('processFile', uploadFile);
      if (excludedFiles.length > 0) {
        formData.append('excludedFiles', JSON.stringify(excludedFiles));
      }
      formData.append('excludeCommentOutActivities', String(excludeCommentOutActivities));
      formData.append('removeArgumentDefaultValues', String(removeArgumentDefaultValues));

      const response = await fetchWithSessionHandling(`/api/processes/${process.id}/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setSyncStatus(`✅ Successfully processed ${data.data.parsedFiles} workflow files!`);
        setUploadFile(null);
        setShowFileExclusion(false);
        setFileExclusionData(null);
        
        onUpdate({
          ...process,
          technicalName: data.data.technicalName,
          version: data.data.version,
          lastSyncCommit: null,
          lastSyncDate: data.data.syncDate
        });
      } else {
        const errorMsg = data.message || 'Upload failed';
        setSyncStatus(`❌ ${errorMsg}`);
        alert('Upload Failed\n\n' + errorMsg);
      }
    } catch (error) {
      setSyncStatus(`❌ Error: ${error.message}`);
    } finally {
      setUploading(false);
      setFileExclusionLoading(false);
    }
  };

  if (process.isEditing) {
    return (
      <div className="relative p-4" style={{border: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface-2)'}}>
        {/* Uploading Overlay - Edit Mode */}
        {(uploading || process.isUploading) && (
          <div className="absolute inset-0 flex items-center justify-center z-10" style={{backgroundColor: 'rgba(34,34,34,0.92)'}}>
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{borderColor: 'var(--tqa-green)'}}></div>
              <p className="mt-3 text-sm font-medium" style={{color: 'var(--tqa-green)'}}>Processing upload...</p>
              <p className="mt-1 text-xs" style={{color: 'rgba(255,255,255,0.4)'}}>Extracting UiPath code</p>
            </div>
          </div>
        )}
        
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1" style={{color: 'rgba(255,255,255,0.6)'}}>
              Process Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={editData.name}
              onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Dispatcher, Performer"
              className="w-full px-3 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
            />
          </div>
          
          <div>
            <label className="block text-xs font-semibold mb-1" style={{color: 'rgba(255,255,255,0.6)'}}>
              Process Description
            </label>
            <input
              type="text"
              value={editData.description}
              onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this process"
              className="w-full px-3 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
            />
          </div>
          
          {/* Source Type Selection (only for new processes) */}
          {process.isNew && (
            <div>
              <label className="block text-xs font-semibold mb-2" style={{color: 'rgba(255,255,255,0.6)'}}>
                Source Type <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sourceType"
                    value="repository"
                    checked={editData.sourceType === 'repository'}
                    onChange={(e) => setEditData(prev => ({ ...prev, sourceType: e.target.value }))}
                    className="mr-2"
                  />
                  <span className="text-sm" style={{color: 'rgba(255,255,255,0.75)'}}>Repository URL</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sourceType"
                    value="manual"
                    checked={editData.sourceType === 'manual'}
                    onChange={(e) => setEditData(prev => ({ ...prev, sourceType: e.target.value }))}
                    className="mr-2"
                  />
                  <span className="text-sm" style={{color: 'rgba(255,255,255,0.75)'}}>Manual Upload</span>
                </label>
              </div>
            </div>
          )}
          
          {/* Repository URL field (shown for repository type or existing processes with URL) */}
          {(editData.sourceType === 'repository' || (!process.isNew && process.repositoryUrl)) && (
            <div>
              <label className="block text-xs font-semibold mb-1" style={{color: 'rgba(255,255,255,0.6)'}}>
                Repository URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={editData.repositoryUrl}
                onChange={(e) => setEditData(prev => ({ ...prev, repositoryUrl: e.target.value }))}
                placeholder="https://bitbucket.org/elementblue/repo-name/src/branch/"
                className="w-full px-3 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              />
            </div>
          )}
          
          {/* Manual Upload field (shown for manual type - new processes or existing manual processes) */}
          {(editData.sourceType === 'manual' || (!process.isNew && !process.repositoryUrl)) && (
            <div>
              <label className="block text-xs font-semibold mb-1" style={{color: 'rgba(255,255,255,0.6)'}}>
                Upload File {process.isNew && <span className="text-red-500">*</span>}
              </label>
              <input
                type="file"
                accept=".zip,.nupkg"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="w-full px-3 py-2 text-sm focus:outline-none rounded-md" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              />
              {uploadFile && (
                <p className="text-xs mt-1" style={{color: 'rgba(255,255,255,0.6)'}}>
                  {uploadFile.name} - {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              )}
              <p className="text-xs mt-1" style={{color: 'rgba(255,255,255,0.3)'}}>
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center space-x-2">
              <button 
                type="button" 
                onClick={handleSave}
                className="btn-primary px-4 py-2 text-sm"
              >
                {process.isNew ? 'Create' : 'Save'}
              </button>
              <button 
                type="button" 
                onClick={onCancel}
                className="btn-secondary px-4 py-2 text-sm"
              >
                Cancel
              </button>
            </div>
            {!process.isNew && (
              <button 
                type="button" 
                onClick={onDelete}
                className="px-4 py-2 text-sm font-medium" style={{color: 'var(--tqa-red)', backgroundColor: 'rgba(230,100,97,0.08)', border: '1px solid rgba(230,100,97,0.3)'}}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 relative" style={{border: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface-2)'}}>
      {/* Syncing Overlay */}
      {process.isSyncing && (
        <div className="absolute inset-0 flex items-center justify-center z-10" style={{backgroundColor: 'rgba(34,34,34,0.92)'}}>
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{borderColor: 'var(--tqa-green)'}}></div>
            <p className="mt-3 text-sm font-medium" style={{color: 'var(--tqa-green)'}}>Syncing repository...</p>
            <p className="mt-1 text-xs" style={{color: 'rgba(255,255,255,0.4)'}}>Extracting UiPath code</p>
          </div>
        </div>
      )}
      
      {/* Uploading Overlay */}
      {(uploading || process.isUploading) && (
        <div className="absolute inset-0 flex items-center justify-center z-10" style={{backgroundColor: 'rgba(34,34,34,0.92)'}}>
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{borderColor: 'var(--tqa-green)'}}></div>
            <p className="mt-3 text-sm font-medium" style={{color: 'var(--tqa-green)'}}>Processing upload...</p>
            <p className="mt-1 text-xs" style={{color: 'rgba(255,255,255,0.4)'}}>Extracting UiPath code</p>
          </div>
        </div>
      )}
      
      <div className="space-y-3">
        {/* Title and Action Buttons Row */}
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold" style={{color: 'rgba(255,255,255,0.85)'}}>{process.name}</h4>
          
          <div className="flex items-center space-x-2">
            <button 
              type="button" 
              onClick={onEdit}
              className="btn-secondary px-4 py-2 text-xs"
            >
              Edit
            </button>
            
            {/* Show Sync button only if process has repository URL */}
            {process.repositoryUrl && (
              <button 
                type="button" 
                onClick={handleSync}
                disabled={syncing || uploading}
                className="btn-primary px-4 py-2 text-xs disabled:opacity-50"
              >
                {syncing ? 'Syncing...' : 'Sync'}
              </button>
            )}
          </div>
        </div>
        
        {/* Description */}
        {process.description && (
          <p className="text-xs" style={{color: 'rgba(255,255,255,0.5)'}}>{process.description}</p>
        )}
        
        {/* Sync Status */}
        {syncStatus && (
          <span className="text-xs" style={{color: 'rgba(255,255,255,0.5)'}}>{syncStatus}</span>
        )}
        
        {/* Unified Process Details Block */}
        <div className="p-3" style={{backgroundColor: 'var(--tqa-surface-3)', border: '1px solid var(--tqa-border-dark)'}}>
          <div className="space-y-2">
            {process.technicalName && (
              <div className="flex items-start">
                <span className="text-xs font-semibold min-w-[140px]" style={{color: 'rgba(255,255,255,0.5)'}}>UiPath Process Name:</span>
                <span className="text-xs font-mono" style={{color: 'rgba(255,255,255,0.85)'}}>{process.technicalName}</span>
              </div>
            )}
            {process.version && (
              <div className="flex items-start">
                <span className="text-xs font-semibold min-w-[140px]" style={{color: 'rgba(255,255,255,0.5)'}}>Process Version:</span>
                <span className="text-xs font-mono" style={{color: 'rgba(255,255,255,0.85)'}}>{process.version}</span>
              </div>
            )}
            {process.repositoryUrl && (
              <div className="flex items-start">
                <span className="text-xs font-semibold min-w-[140px]" style={{color: 'rgba(255,255,255,0.5)'}}>Repository:</span>
                <a 
                  href={process.repositoryUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-xs hover:underline break-all" style={{color: 'var(--tqa-green)'}}
                >
                  {process.repositoryUrl}
                </a>
              </div>
            )}
            {process.lastSyncDate && (
              <>
                <div className="flex items-start">
                  <span className="text-xs font-semibold min-w-[140px]" style={{color: 'rgba(255,255,255,0.5)'}}>Last Synced:</span>
                  <span className="text-xs" style={{color: 'rgba(255,255,255,0.85)'}}>
                    {new Date(process.lastSyncDate).toLocaleString()}
                  </span>
                </div>
                {process.lastSyncCommit && (
                  <div className="flex items-start">
                    <span className="text-xs font-semibold min-w-[140px]" style={{color: 'rgba(255,255,255,0.5)'}}>Commit:</span>
                    <span className="text-xs font-mono" style={{color: 'rgba(255,255,255,0.85)'}}>{process.lastSyncCommit.substring(0, 7)}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sync Disclaimer Modal */}
      <SyncDisclaimerModal
        isOpen={showSyncDisclaimerModal}
        onConfirm={() => {
          setShowSyncDisclaimerModal(false);
          if (pendingSyncAction) {
            pendingSyncAction();
            setPendingSyncAction(null);
          }
        }}
        onCancel={() => {
          setShowSyncDisclaimerModal(false);
          setPendingSyncAction(null);
          setSyncStatus('❌ Sync cancelled');
          setTimeout(() => setSyncStatus(''), 2000);
        }}
      />

      <MissingPackagesModal
        isOpen={showMissingPackagesModal}
        packages={previewPackageList}
        onClose={() => {
          setShowMissingPackagesModal(false);
          setPreviewPackageList([]);
          setFileExclusionData(null);
          setPendingExclusionAction(null);
        }}
        onContinue={() => {
          setShowMissingPackagesModal(false);
          setShowFileExclusion(true);
        }}
      />

      {/* File Exclusion Modal (for re-uploads and repository syncs on existing processes) */}
      <FileExclusionModal
        isOpen={showFileExclusion}
        onClose={() => {
          setShowFileExclusion(false);
          setFileExclusionData(null);
          setPendingExclusionAction(null);
        }}
        onConfirm={({ excludedFiles, excludeCommentOutActivities, removeArgumentDefaultValues }) => {
          if (pendingExclusionAction) {
            pendingExclusionAction({ excludedFiles, excludeCommentOutActivities, removeArgumentDefaultValues });
          }
        }}
        fileTree={fileExclusionData?.fileTree}
        projectName={fileExclusionData?.projectName}
        totalFiles={fileExclusionData?.totalFiles || 0}
        loading={fileExclusionLoading}
      />
    </div>
  );
};

// Document Item Component
const DocumentItem = ({ document, onSave, onEdit, onRemove, onCancel }) => {
  const [editData, setEditData] = useState({
    type: document.type || 'link',
    url: document.url || '',
    file: null,
    description: document.description || ''
  });

  const handleSave = () => {
    if (!editData.description.trim()) return;
    if (editData.type === 'link' && !editData.url.trim()) return;
    if (editData.type === 'file' && !editData.file && !document.fileName) return;
    
    onSave(document.id, editData);
  };

  if (document.isEditing) {
    return (
      <li className="p-4">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-1 text-xs font-medium">
              <input 
                type="radio" 
                name={`doc-type-${document.id}`} 
                value="link"
                checked={editData.type === 'link'}
                onChange={(e) => setEditData(prev => ({ ...prev, type: 'link' }))}
              />
              <span>Link</span>
            </label>
            <label className="flex items-center space-x-1 text-xs font-medium">
              <input 
                type="radio" 
                name={`doc-type-${document.id}`} 
                value="file"
                checked={editData.type === 'file'}
                onChange={(e) => setEditData(prev => ({ ...prev, type: 'file' }))}
              />
              <span>File</span>
            </label>
          </div>
          
          <div className="flex-1 space-y-2">
            {editData.type === 'link' ? (
              <input
                type="url"
                value={editData.url}
                onChange={(e) => setEditData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://docs.example.com/spec"
                className="w-full px-3 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              />
            ) : (
              <input
                type="file"
                onChange={(e) => setEditData(prev => ({ ...prev, file: e.target.files[0] }))}
                className="w-full px-3 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
              />
            )}
            <input
              type="text"
              value={editData.description}
              onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Short description (e.g., Solution Design Spec)"
              className="w-full px-3 py-2 text-sm focus:outline-none" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)', color: 'rgba(255,255,255,0.85)'}}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <button 
              type="button" 
              onClick={handleSave}
              className="btn-primary px-4 py-2 text-sm"
            >
              Save
            </button>
            <button 
              type="button" 
              onClick={() => onCancel(document.id)}
              className="btn-secondary px-4 py-2 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="p-4 flex flex-col md:flex-row md:items-start md:space-x-4 space-y-3 md:space-y-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium" style={{color: 'rgba(255,255,255,0.85)'}}>
          {document.type === 'link' ? (
            <a href={document.url} target="_blank" rel="noopener noreferrer" className="hover:underline break-all" style={{color: 'var(--tqa-green)'}}>
              {document.url}
            </a>
          ) : (
            document.fileName || '(file)'
          )}
        </p>
        <p className="text-xs mt-1" style={{color: 'rgba(255,255,255,0.4)'}}>
          {document.description} • {document.type === 'link' ? 'Link' : 'File'}
        </p>
      </div>
      <div className="flex items-center space-x-2">
        <button 
          type="button" 
          onClick={() => onEdit(document.id)}
          className="btn-secondary px-4 py-2 text-xs"
        >
          Edit
        </button>
        <button 
          type="button" 
          onClick={() => onRemove(document.id)}
          className="btn-secondary px-4 py-2 text-xs"
        >
          Remove
        </button>
      </div>
    </li>
  );
};

// Delete Process Confirmation Modal Component
const DeleteProcessModal = ({ isOpen, processName, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="shadow-xl p-6 w-96" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
        <h3 className="text-lg font-semibold mb-4" style={{color: 'rgba(255,255,255,0.9)'}}>Delete Process</h3>
        <p className="text-sm mb-6" style={{color: 'rgba(255,255,255,0.65)'}}>
          Are you sure you want to delete the process <strong>"{processName}"</strong>?
          <br /><br />
          This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary px-4 py-2 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white" style={{backgroundColor: 'var(--tqa-red)'}}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Sync Disclaimer Modal Component
const SyncDisclaimerModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="shadow-xl p-6 w-[500px]" style={{backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)'}}>
        <div className="flex items-start mb-4">
          <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center" style={{backgroundColor: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)'}}>
            <svg className="w-6 h-6" style={{color: 'rgba(245,158,11,0.9)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-lg font-semibold" style={{color: 'rgba(255,255,255,0.9)'}}>Data Privacy Notice</h3>
          </div>
        </div>
        <div className="text-sm mb-6 space-y-3" style={{color: 'rgba(255,255,255,0.65)'}}>
          <p>
            <strong>Important:</strong> The process code you're about to sync will be analyzed using <strong>Google Gemini AI</strong>.
          </p>
          <p>
            Please ensure that your XAML workflow files and configuration files <strong>do not contain any sensitive information</strong>, including:
          </p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Personal Identifiable Information (PII)</li>
            <li>Protected Health Information (PHI)</li>
            <li>Credentials, API keys, or passwords</li>
            <li>Confidential business data</li>
          </ul>
          <p className="text-xs mt-3" style={{color: 'rgba(255,255,255,0.4)'}}>
            By proceeding, you confirm that the code being uploaded is safe to analyze with third-party AI services.
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary px-4 py-2 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="btn-primary px-4 py-2 text-sm font-medium"
          >
            I Understand, Proceed
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectConfigTab;